'use client';

import React, { useState, useEffect, useRef } from 'react';
import Icon from '@/components/ui/AppIcon';

interface UseCase {
    label: string;
    icon: string;
    title: string;
    desc: string;
    code: string;
    restoreCommand: string;
    accent: string;
    accentBg: string;
}

const useCases: UseCase[] = [
    {
        label: 'Functions',
        icon: 'CodeBracketIcon',
        title: 'Pure functions & utilities',
        desc: 'Restore any standalone function to a previous working state without touching the module it lives in.',
        code: `function calculateDiscount(
  price: number,
  tier: UserTier
): number {
  // v1: simple percentage
  return price * 0.1;
}`,
        restoreCommand: 'capstate restore calculateDiscount@11:02am',
        accent: 'text-primary',
        accentBg: 'bg-primary/10',
    },
    {
        label: 'React Components',
        icon: 'CubeIcon',
        title: 'UI components',
        desc: 'Broke a component refactor? Restore the component tree to any point in its history, keeping your other components intact.',
        code: `export function PriceTag({ amount }: Props) {
  return (
    <span className="price">
      {formatCurrency(amount)}
    </span>
  );
}`,
        restoreCommand: 'capstate restore PriceTag@yesterday',
        accent: 'text-accent',
        accentBg: 'bg-accent/10',
    },
    {
        label: 'API Handlers',
        icon: 'ServerIcon',
        title: 'Route & request handlers',
        desc: 'API endpoints evolve fast. Restore a specific handler to a known-good version while your new endpoints stay live.',
        code: `app.post('/api/checkout', async (req, res) => {
  const { items, userId } = req.body;
  const order = await createOrder(items);
  res.json({ orderId: order.id });
});`,
        restoreCommand: 'capstate restore checkoutHandler@v2',
        accent: 'text-purple-400',
        accentBg: 'bg-purple-400/10',
    },
    {
        label: 'Algorithms',
        icon: 'CpuChipIcon',
        title: 'Core logic & algorithms',
        desc: 'Performance regression in a sorting or search algorithm? Roll back the logic without touching the data layer.',
        code: `function rankResults(
  results: SearchResult[]
): SearchResult[] {
  return results
    .sort((a, b) => b.score - a.score)
    .slice(0, 20);
}`,
        restoreCommand: 'capstate restore rankResults@before-refactor',
        accent: 'text-orange-400',
        accentBg: 'bg-orange-400/10',
    },
    {
        label: 'Classes',
        icon: 'RectangleStackIcon',
        title: 'Classes & services',
        desc: 'Service classes grow complex. Restore an individual method or the entire class body to a previous checkpoint.',
        code: `class PaymentService {
  async charge(
    amount: number,
    method: PaymentMethod
  ): Promise<Receipt> {
    return this.gateway.process(amount);
  }
}`,
        restoreCommand: 'capstate restore PaymentService.charge@2:41pm',
        accent: 'text-primary',
        accentBg: 'bg-primary/10',
    },
    {
        label: 'Config Blocks',
        icon: 'AdjustmentsHorizontalIcon',
        title: 'Config & feature flags',
        desc: 'Revert a misconfigured feature flag or build config block independently of everything else in the file.',
        code: `export const featureFlags = {
  newCheckout: true,
  betaPricing: false,
  darkMode: true,
  experimentalAPI: false,
};`,
        restoreCommand: 'capstate restore featureFlags@before-deploy',
        accent: 'text-accent',
        accentBg: 'bg-accent/10',
    },
];

export default function UseCasesSection() {
    const [activeCard, setActiveCard] = useState<number | null>(null);
    const sectionRef = useRef<HTMLElement>(null);

    useEffect(() => {
        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                        const cards = entry.target.querySelectorAll<HTMLElement>('.usecase-card');
                        cards.forEach((card, i) => {
                            setTimeout(() => {
                                card.style.opacity = '1';
                                card.style.transform = 'translateY(0)';
                            }, i * 80);
                        });
                    }
                });
            },
            { threshold: 0.1 }
        );
        if (sectionRef.current) observer.observe(sectionRef.current);
        return () => observer.disconnect();
    }, []);

    return (
        <section id="use-cases" ref={sectionRef} className="py-24 px-6 relative">
            <div className="max-w-7xl mx-auto">
                <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-14">
                    <div className="max-w-xl">
                        <p className="section-label mb-4">Developer Workflow</p>
                        <h2 className="text-section-heading font-extrabold text-foreground">
                            Every block type,{' '}
                            <span className="text-gradient-primary italic font-light">covered.</span>
                        </h2>
                    </div>
                    <p className="text-muted-foreground max-w-sm text-base leading-relaxed md:text-right">
                        CapState tracks history at the level you actually think about code — not files, not
                        commits, but the blocks that matter.
                    </p>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    {useCases.map((uc, i) => (
                        <div
                            key={i}
                            className="usecase-card bento-card group cursor-pointer relative overflow-hidden transition-all duration-300"
                            style={{ opacity: 1, transform: 'translateY(0)' }}
                            onMouseEnter={() => setActiveCard(i)}
                            onMouseLeave={() => setActiveCard(null)}
                        >
                            {/* Default state */}
                            <div
                                className={`transition-opacity duration-300 ${activeCard === i ? 'opacity-0' : 'opacity-100'
                                    }`}
                            >
                                <div className="flex items-center gap-3 mb-4">
                                    <div
                                        className={`w-9 h-9 rounded-xl ${uc.accentBg} flex items-center justify-center`}
                                    >
                                        <Icon name={uc.icon as any} size={18} className={uc.accent} />
                                    </div>
                                    <span className="font-mono-code text-xs text-muted-foreground">{uc.label}</span>
                                </div>
                                <h3 className="font-bold text-foreground text-base mb-2">{uc.title}</h3>
                                <p className="text-sm text-muted-foreground leading-relaxed">{uc.desc}</p>
                            </div>

                            {/* Hover state: code preview */}
                            <div
                                className={`absolute inset-0 p-5 flex flex-col justify-between transition-opacity duration-300 ${activeCard === i ? 'opacity-100' : 'opacity-0 pointer-events-none'
                                    }`}
                            >
                                <div className="bg-background/80 rounded-xl border border-border p-3 overflow-hidden flex-1 mb-3">
                                    <pre className="code-block text-xs leading-relaxed whitespace-pre-wrap text-foreground/80 overflow-hidden">
                                        {uc.code}
                                    </pre>
                                </div>
                                <div className="bg-card rounded-xl border border-border px-3 py-2 flex items-center gap-2">
                                    <span className="text-accent font-mono-code text-xs">$</span>
                                    <span className="font-mono-code text-xs text-foreground/70 truncate">
                                        {uc.restoreCommand}
                                    </span>
                                </div>
                            </div>

                            {/* Hover border glow */}
                            <div
                                className={`absolute inset-0 rounded-2xl border transition-all duration-300 pointer-events-none ${activeCard === i ? `border-${uc.accent.split('-')[1]}-400/30` : 'border-transparent'
                                    }`}
                            />
                        </div>
                    ))}
                </div>
            </div>
        </section>
    );
}